<script lang="ts">
  /* CookieConsent — the app's cookie bar. The app itself sets only the session
     and CSRF cookies; Google Analytics (the same GA4 property as the landing,
     so a visitor coming from orderevolution.com stays one user) is injected
     only after «Accept all». The choice lives in localStorage for 90 days, the
     same key and shape as the landing's bar. A browser that blocks storage
     sees the bar on every visit and never loads the tag.

     Page views are sent by hand on every hash-route change with
     send_page_view off, so the SPA is not blind after the first screen and
     enhanced measurement cannot double-count. */
  import { _ } from "svelte-i18n";
  import { router } from "svelte-spa-router";

  const KEY = "oe_cookie_consent";
  const DAYS = 90;
  const GA_ID = "G-M06G6C86CN";

  let visible = $state(false);
  let analytics = $state(false);

  function stored(): { analytics: boolean } | null {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || "null");
      if (!v || typeof v !== "object") return null;
      const t = Date.parse(v.at || "");
      return !isNaN(t) && Date.now() - t < DAYS * 86400000 ? { analytics: !!v.analytics } : null;
    } catch {
      return null;
    }
  }

  function loadGa() {
    if (document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`)) return;
    const g = document.createElement("script");
    g.async = true;
    g.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(g);
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.gtag = function () {
      w.dataLayer.push(arguments);
    };
    // Consent mode: the bar asked for analytics only, so ads signals stay
    // denied; without this a loaded tag assumes everything is granted.
    w.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    w.gtag("js", new Date());
    w.gtag("config", GA_ID, { send_page_view: false });
    analytics = true;
  }

  function remember(accept: boolean) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ analytics: accept, at: new Date().toISOString() }));
    } catch {
      /* storage blocked: the bar simply returns next visit */
    }
    visible = false;
    if (accept) loadGa();
  }

  $effect(() => {
    const choice = stored();
    if (choice) {
      if (choice.analytics) loadGa();
      return;
    }
    visible = true;
  });

  // One page_view per route, with the hash as the path.
  $effect(() => {
    const loc = router.location;
    if (!analytics) return;
    const w = window as any;
    if (typeof w.gtag !== "function") return;
    w.gtag("event", "page_view", {
      page_path: `/#${loc}`,
      page_location: window.location.href,
      page_title: document.title,
    });
  });
</script>

{#if visible}
  <div class="cookie" role="region" aria-label={$_("cookie.label")}>
    <p>
      {$_("cookie.text")}
      <a href="#/privacy?from=app" target="_blank" rel="noopener">{$_("consent.privacy")}</a>.
    </p>
    <div class="cookie-actions">
      <button type="button" class="btn btn--ghost cookie-ghost" onclick={() => remember(false)}>{$_("cookie.necessaryOnly")}</button>
      <button type="button" class="btn btn--primary" onclick={() => remember(true)}>{$_("cookie.acceptAll")}</button>
    </div>
  </div>
{/if}

<style>
  .cookie {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 2500;
    background: var(--color-surface-darker); color: #e6eaee;
    box-shadow: 0 -8px 24px rgba(0,0,0,0.18); font-size: 14px;
    display: flex; align-items: center; justify-content: space-between; gap: 24px;
    padding: 14px 24px;
  }
  .cookie p { margin: 0; line-height: 1.4; }
  .cookie a { color: #fff; text-decoration: underline; text-underline-offset: 2px; }
  .cookie-actions { display: flex; gap: 10px; flex-shrink: 0; }
  .cookie-ghost { color: #fff; border-color: rgba(255,255,255,0.35); }
  @media (max-width: 720px) {
    .cookie { flex-direction: column; align-items: flex-start; gap: 12px; padding: 14px 16px; }
  }
</style>
